/**
 * Basemag series model, import quality rules, edge trim and chart decimation (phase 2b.2; spec
 * docs/specs/basemag-import.md). Parsers are native (crates/teslyn-core/src/basemag); this module defines the
 * shapes they return and the rules both sides share (`basemag-rules.json`, read by Rust with `include_str!`).
 *
 * Readings are UTC instants (epoch ms), values in nT and a flag byte. Stored data is never trimmed or thinned at
 * random; the one reduction is the import rate the person chooses (decision 225, `reduceToRate`). The trim and the
 * decimation below are for the chart and the QC flags only.
 */
import rules from './basemag-rules.json';
import { localParts, type ClockKind } from './time-model.js';
import type { Common, Uuid } from './model.js';

export const BASEMAG_RULES = rules;

/** Reading flags (bits). */
export const FLAG_QUESTIONABLE = 1; // 857 reading marked "?" (kept, decision 22)
export const FLAG_STATUS = 2; // 862 status other than 00
/** MagStation CSV row without date or time, timed from the counter: only in series stored by parser `basemag-3` or
 * earlier; since decision 219 (Q193, `basemag-4`) such rows are left out like the `.dbt` readings outside the PPS
 * pulses. */
export const FLAG_TIME_FROM_COUNTER = 4;

/**
 * `observatory_iaga`: observatory data in IAGA-2002 (INTERMAGNET, BGS, USGS), read as the total field the
 * observatory reports and never shifted by a reference value (MB decision 154, Q16; the Diurnal tool's bug B-9).
 */
export type BasemagFormat =
  | 'magstation_dbt'
  | 'magstation_csv'
  | 'g857_raw'
  | 'g823_dat'
  | 'g862_txt'
  | 'observatory_iaga'
  /** BoM Space Weather Services raw `mK`/`mG` files, the total of their variations (decision 227). */
  | 'observatory_bom'
  /** BGS INTERMAGNET 1-second data as Teslyn saves a HAPI download (decision 227). */
  | 'observatory_hapi';

export const BASEMAG_FORMAT_LABEL: Readonly<Record<BasemagFormat, string>> = {
  magstation_dbt: 'MagStation .dbt',
  magstation_csv: 'MagStation CSV',
  g857_raw: '857/856 .raw',
  g823_dat: '823 .DAT',
  g862_txt: '862 .txt',
  observatory_iaga: 'Observatory (IAGA-2002)',
  observatory_bom: 'Observatory (BoM raw)',
  observatory_hapi: 'Observatory (BGS 1-second)',
};

/** One recording session inside a file (a MagStation `dataset`; other formats have one). */
export interface BasemagSession {
  /** The session's number in the file (a MagStation `dataset`, in file order), from 1. */
  readonly index: number;
  readonly native_hz: number | null;
  /** Stored readings of the session (in `basemag_inspect`: the records in the file). */
  readonly reading_count: number;
  readonly started_at: string;
  readonly ended_at: string;
  /** The rate of the stored readings (decision 225); absent in series stored before parser `basemag-5`. */
  readonly imported_hz?: number | null;
  /** Readings recorded per stored reading (1: every reading kept); absent before `basemag-5`. */
  readonly group?: number;
}

export type QualityLevel = 'warn' | 'info';

export type QualityKind =
  | 'gap'
  | 'jump'
  | 'time_back'
  | 'questionable'
  | 'status_flag'
  | 'invalid_main'
  /** Only in reports stored by parser `basemag-3` or earlier (decision 219: CSV rows with no time are left out). */
  | 'time_from_counter'
  | 'unreadable_lines'
  /** 823 readings logged while the GPS had no time; a MagStation CSV row with no time between timed rows. */
  | 'no_time'
  | 'gps_lost'
  /** MagStation readings left out: record counter not above the last kept one (decision 177). */
  | 'record_counter_back'
  /**
   * MagStation record counter skips forward: readings missing, not filled (decision 177). Readings left out just
   * before the skip are not counted as missing ("6 readings missing, 2 left out", issue #23; parser `basemag-3`).
   */
  | 'record_counter_gap'
  /**
   * MagStation readings before the first or after the last PPS pulse with a GPS fix: no time, left out and reported,
   * as Geometrics Survey Manager does (decision 202; parser `basemag-3`). `count` is readings, one location per
   * session and side. A Geometrics CSV row with no date or time is the same reading and is reported the same way
   * (decision 219). A note since parser `basemag-4` (decision 220; a warning before), and not reported at all when
   * the readings lie inside the project's edge trim at the start or end of the data (`edgeTrimMin`, decision 24).
   */
  | 'outside_pps'
  /** Sessions of one .dbt that overlap in time (decision 177). */
  | 'session_overlap'
  /** Only in reports stored by parser `basemag-1` (even spacing, decision 28, replaced by decision 177). */
  | 'record_counter_jump'
  | 'rate_mismatch'
  | 'name_day_mismatch'
  | 'mixed_rates'
  | 'sessions'
  | 'no_pps'
  | 'skipped_rows'
  | 'year_assumed'
  | 'missing_values'
  | 'total_field_computed'
  /** Stored at a lower rate than recorded: the median of each group of readings (decision 225, `basemag-5`). */
  | 'import_rate'
  /** Only the sessions chosen came in (decision 226). */
  | 'sessions_chosen'
  /** BoM raw: the total computed from variations does not follow the field's change (decision 227). */
  | 'variation_total'
  /** BGS 1-second: a request of the day could not be downloaded (decision 227). */
  | 'download_incomplete';

export interface QualityLocation {
  /** UTC instant (epoch ms) of the reading, or the start of a gap. */
  readonly at_ms: number | null;
  /** End of a gap (epoch ms). */
  readonly end_ms: number | null;
  /** Line of the file, when the finding is about a line. */
  readonly line: number | null;
  readonly note: string | null;
}

export interface QualityItem {
  readonly kind: QualityKind;
  readonly level: QualityLevel;
  /** Plain words for the crew; numbers with units. */
  readonly message: string;
  readonly count: number;
  /** Up to `listLimit` locations. */
  readonly locations: readonly QualityLocation[];
}

export interface QualityReport {
  readonly reading_count: number;
  readonly median_interval_ms: number | null;
  readonly gap_threshold_ms: number | null;
  readonly items: readonly QualityItem[];
}

/** A stored basemag file (`basemag_series` row). Readings are stored apart (`basemag_readings`). */
export interface BasemagSeries extends Common {
  readonly project_id: Uuid;
  readonly file_name: string;
  readonly source_path: string;
  readonly source_sha256: string;
  readonly source_bytes: number;
  /** A file's format, or `ta_live` for a TA Live day kept in the project (N28, SQLite 0058). */
  readonly source_format: BasemagFormat | 'ta_live';
  /** Instrument family (`MagStation`, `G-857`, `G-823`, `G-862`). */
  readonly instrument: string;
  readonly instrument_serial: string | null;
  readonly firmware: string | null;
  /** MagStation: always the main sensor (decision 2); null for single-sensor instruments. */
  readonly sensor_channel: 'main' | null;
  /** Thomson export station id (MagStation 60, 823 82, 862 86, 857 from the file). */
  readonly station_id: string;
  readonly clock: ClockKind;
  /** Project offset in effect at import (the local clock of 857 and XYZ files is read in it). */
  readonly utc_offset_h: number;
  readonly native_hz: number | null;
  /**
   * Rate stored: the chosen import rate (decision 225: the median of each group of readings, 1 Hz by default), the
   * native rate when every reading was kept, 1 Hz for 823 and 862 (first reading per second, decision 21).
   */
  readonly imported_hz: number | null;
  readonly parser_version: string;
  readonly sessions: readonly BasemagSession[];
  readonly reading_count: number;
  readonly started_at: string;
  readonly ended_at: string;
  readonly value_min: number | null;
  readonly value_max: number | null;
  readonly quality: QualityReport;
}

/** A survey line from a `_Diurnal.XYZ` file (`diurnal_line` row). */
export interface DiurnalLine extends Common {
  readonly project_id: Uuid;
  readonly source_path: string;
  readonly source_sha256: string;
  /** File name without `_Diurnal.XYZ`, for example `103060117` (flight 103, started 06:01:17). */
  readonly flight_tag: string;
  /** Line number as written, for example `10190.0`. */
  readonly line: string;
  readonly jday: number;
  readonly start_hms: string;
  readonly end_hms: string;
  readonly start_at: string;
  readonly end_at: string;
  readonly utc_offset_h: number;
  readonly test_line: boolean;
}

/**
 * The flight number of a survey-line file's tag as text: the digits before the six of the start time
 * (`103060117` → `103`, `1000060117` → `1000`, so a 1,000th flight is never read as flight 100), or the tag itself
 * when it is a bare flight number (`002`), or its first three characters when it is not digits at all.
 */
export function flightTagNumberText(tag: string): string {
  const t = tag.trim();
  if (/^\d+$/.test(t)) return t.length > 6 ? t.slice(0, t.length - 6) : t;
  return t.slice(0, 3);
}

/** The flight number of a survey-line file's tag or a bare flight number; null when it is not a number. */
export function flightNumberOfTag(tag: string): number | null {
  const n = Number.parseInt(flightTagNumberText(tag), 10);
  return Number.isFinite(n) ? n : null;
}

/** What `basemag_inspect` returns for one path: what the import choices need (decisions 225 and 226). */
export interface BasemagInspectFile {
  readonly path: string;
  /** Null for a survey-line file or a file that cannot be read. */
  readonly format: BasemagFormat | null;
  /** A `.dbt`'s sessions from a scan of the file (times and counts close, not exact); other formats one. */
  readonly sessions: readonly BasemagSession[];
  readonly error: { readonly code: string; readonly message: string } | null;
  /**
   * For a file with day numbers and no year (857 `.raw`, `_Diurnal.XYZ`): the year Teslyn will read it in and
   * where that came from (decision 488), shown before the import. Absent for files that carry their dates.
   */
  readonly year?: BasemagYearNote | null;
}

/** The year chosen for a file without one (decision 488). */
export interface BasemagYearNote {
  readonly year: number;
  /** The file's first day number. */
  readonly jday: number;
  /** "D005.dbt in the same folder", "FLT040.LOG of that day", "the nearest of the project's flights and entries". */
  readonly from: string;
}

/** "D005_Mag_Station.raw has no year: read as day 5 of 2027 (from D005.dbt in the same folder)." */
export function yearNoteText(file: BasemagInspectFile): string | null {
  if (!file.year) return null;
  const name = file.path.split(/[\\/]/).pop() ?? file.path;
  return `${name} has no year: read as day ${file.year.jday} of ${file.year.year} (from ${file.year.from}).`;
}

/** What `basemag_import` returns for one path. */
export interface BasemagImportFile {
  readonly path: string;
  readonly series: BasemagSeries | null;
  /** Survey lines read from this path (an `.xyz`) or from the Day-folder scan it started. */
  readonly survey_lines: number;
  readonly survey_line_files: readonly string[];
  /** True when the same file content had been imported before and was replaced. */
  readonly replaced: boolean;
  readonly error: { readonly code: string; readonly message: string } | null;
}

// ---------------------------------------------------------------------------------------------- quality rules

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** Gap threshold: max(1 s, 5 × the median interval), ms. */
export function gapThresholdMs(medianIntervalMs: number): number {
  return Math.max(rules.gapMinS * 1000, rules.gapFactor * medianIntervalMs);
}

const minutes = (ms: number): string => {
  const s = Math.round(ms / 1000);
  if (s < 120) return `${s} s`;
  const m = s / 60;
  return m < 120 ? `${Math.round(m * 10) / 10} min` : `${Math.round((m / 60) * 10) / 10} h`;
};

/**
 * Gaps, jumps and time steps back over a series (the generic part of the quality report; the parsers add their own
 * findings). Same rules as crates/teslyn-core/src/basemag/quality.rs.
 */
export function analyseReadings(tMs: readonly number[], values: readonly number[]): QualityReport {
  const n = tMs.length;
  const positive: number[] = [];
  for (let i = 1; i < n; i++) {
    const d = tMs[i]! - tMs[i - 1]!;
    if (d > 0) positive.push(d);
  }
  const med = median(positive);
  const items: QualityItem[] = [];
  if (med !== null) {
    const threshold = gapThresholdMs(med);
    const gaps: QualityLocation[] = [];
    const back: QualityLocation[] = [];
    let gapCount = 0;
    let backCount = 0;
    let longest = 0;
    for (let i = 1; i < n; i++) {
      const d = tMs[i]! - tMs[i - 1]!;
      if (d > threshold) {
        gapCount++;
        longest = Math.max(longest, d);
        if (gaps.length < rules.listLimit)
          gaps.push({ at_ms: tMs[i - 1]!, end_ms: tMs[i]!, line: null, note: null });
      } else if (d <= 0) {
        backCount++;
        if (back.length < rules.listLimit)
          back.push({ at_ms: tMs[i]!, end_ms: null, line: null, note: null });
      }
    }
    if (gapCount > 0) {
      items.push({
        kind: 'gap',
        level: 'warn',
        message: `${gapCount} ${gapCount === 1 ? 'gap' : 'gaps'} longer than ${minutes(threshold)}; the longest is ${minutes(longest)}.`,
        count: gapCount,
        locations: gaps,
      });
    }
    if (backCount > 0) {
      items.push({
        kind: 'time_back',
        level: 'warn',
        message: `The time steps back or repeats ${backCount} ${backCount === 1 ? 'time' : 'times'}.`,
        count: backCount,
        locations: back,
      });
    }
    const jumps: QualityLocation[] = [];
    let jumpCount = 0;
    for (let i = 1; i < n; i++) {
      const dv = values[i]! - values[i - 1]!;
      if (Math.abs(dv) > rules.jumpNt) {
        jumpCount++;
        if (jumps.length < rules.listLimit) {
          jumps.push({
            at_ms: tMs[i]!,
            end_ms: null,
            line: null,
            note: `${dv > 0 ? '+' : '−'}${Math.abs(Math.round(dv * 10) / 10)} nT`,
          });
        }
      }
    }
    if (jumpCount > 0) {
      items.push({
        kind: 'jump',
        level: 'warn',
        message: `${jumpCount} ${jumpCount === 1 ? 'jump' : 'jumps'} of more than ${rules.jumpNt} nT between readings.`,
        count: jumpCount,
        locations: jumps,
      });
    }
    return { reading_count: n, median_interval_ms: med, gap_threshold_ms: threshold, items };
  }
  return { reading_count: n, median_interval_ms: null, gap_threshold_ms: null, items };
}

/** Warnings first, then information. */
export function qualityWarnings(report: QualityReport): readonly QualityItem[] {
  return report.items.filter((i) => i.level === 'warn');
}

/** One line for a file in a list: "No problems found" or "3 warnings". */
export function qualitySummary(report: QualityReport): string {
  const w = qualityWarnings(report).length;
  if (w === 0) return 'No problems found';
  return `${w} ${w === 1 ? 'warning' : 'warnings'}`;
}

/** Items whose `count` is readings, not places: their places are their locations (issue #24). */
const COUNTS_READINGS: ReadonlySet<QualityKind> = new Set<QualityKind>([
  'record_counter_back',
  'outside_pps',
]);

/**
 * How many places of an item a list showing its first `shown` locations leaves out ("and 3 more"). Locations are
 * cut at `listLimit`, so for items that count places the count is the total; for items that count readings
 * (`record_counter_back`, `outside_pps`) the locations are the places (issue #24).
 */
export function placesNotShown(item: QualityItem, shown: number): number {
  const places = COUNTS_READINGS.has(item.kind)
    ? item.locations.length
    : Math.max(item.count, item.locations.length);
  return Math.max(0, places - shown);
}

// ---------------------------------------------------------------------------------------------- edge trim (I10)

export interface TrimRange {
  /** First reading shown (index). */
  readonly start: number;
  /** One past the last reading shown. */
  readonly end: number;
  /** False when the series is no longer than twice the trim, so nothing is hidden. */
  readonly trimmed: boolean;
}

/**
 * Readings shown on the chart and used for QC flags after hiding `trimMin` minutes at each end (decision 24: one
 * setting for every file type). The stored and exported data are never trimmed.
 */
export function edgeTrimRange(tMs: ArrayLike<number>, trimMin: number): TrimRange {
  const n = tMs.length;
  const trimMs = Math.max(0, trimMin) * 60_000;
  if (n === 0 || trimMs === 0) return { start: 0, end: n, trimmed: false };
  const first = tMs[0]!;
  const last = tMs[n - 1]!;
  if (last - first <= 2 * trimMs) return { start: 0, end: n, trimmed: false };
  let start = 0;
  while (start < n && tMs[start]! < first + trimMs) start++;
  let end = n;
  while (end > start && tMs[end - 1]! > last - trimMs) end--;
  return { start, end, trimmed: true };
}

// ---------------------------------------------------------------------------------------------- decimation (I14)

/**
 * Indices to draw when a series has more than `maxPoints` readings: the minimum and maximum of each of
 * ⌊maxPoints / 2⌋ equal index buckets, plus the first and last reading, ascending. Deterministic (B-12: the Diurnal
 * tool thinned at random). Same algorithm as crates/teslyn-core/src/basemag/decimate.rs.
 */
export function decimateMinMax(
  values: readonly number[],
  maxPoints: number = rules.chartMaxPoints,
): number[] {
  const n = values.length;
  if (n <= Math.max(2, maxPoints)) return Array.from({ length: n }, (_, i) => i);
  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const keep = new Set<number>([0, n - 1]);
  for (let b = 0; b < buckets; b++) {
    const lo = Math.floor((b * n) / buckets);
    const hi = Math.floor(((b + 1) * n) / buckets);
    let iMin = -1;
    let iMax = -1;
    for (let i = lo; i < hi; i++) {
      const v = values[i]!;
      if (Number.isNaN(v)) continue;
      if (iMin < 0 || v < values[iMin]!) iMin = i;
      if (iMax < 0 || v > values[iMax]!) iMax = i;
    }
    if (iMin >= 0) keep.add(iMin);
    if (iMax >= 0) keep.add(iMax);
  }
  return [...keep].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------------------------- import rate (decision 225)

/** The rates that may be offered on import, Hz (1, 5, 10; decision 265); "All" keeps every reading. */
export const IMPORT_RATES_HZ: readonly number[] = rules.importRatesHz;
/** The rate an import starts from: 1 Hz. */
export const DEFAULT_IMPORT_HZ: number = rules.importRateDefaultHz;

/** The import rate as sent to `basemag_import`: Hz, or every reading. */
export type ImportRate = number | 'all';

/**
 * Readings per stored reading: round(native / chosen), at least 1; 1 when the native rate is not above the chosen
 * one (the Diurnal tool's `Math.round(currentHz / targetHz)`); exact for every rate that divides the native one.
 * Same rule as crates/teslyn-core/src/basemag/rate.rs.
 */
export function importGroupSize(nativeHz: number | null, targetHz: number): number {
  if (nativeHz === null || !(nativeHz > 0) || !(targetHz > 0) || nativeHz <= targetHz) return 1;
  return Math.max(1, Math.floor(nativeHz / targetHz + 0.5));
}

/**
 * Whether a session at `nativeHz` can come in at `targetHz` (decision 265): the rate divides the native one
 * exactly, or the session is not faster (it keeps every reading). Same rule as `rate::divides` in Rust.
 */
export function importRateDivides(nativeHz: number | null, targetHz: number): boolean {
  if (nativeHz === null || !(nativeHz > 0) || nativeHz <= targetHz) return true;
  const q = nativeHz / targetHz;
  return Math.abs(q - Math.round(q)) < 1e-9;
}

export interface ImportRateOption {
  readonly value: ImportRate;
  readonly label: string;
}

function hzWords(hz: number): string {
  const r = Math.round(hz * 100) / 100;
  return `${r} Hz`;
}

/**
 * The rates to offer for an import whose sessions record at `sessionsHz` (decision 265): each of 1, 5 and 10 Hz
 * below the fastest session that divides every session faster than it exactly (a session at or below the rate
 * keeps every reading), then "All". 20 Hz data: 1, 5, 10 Hz; 10 Hz: 1, 5 Hz (10 Hz is All); 25 Hz: 1, 5 Hz;
 * 5 Hz: 1 Hz; 1 Hz or slower: only All. The label gives the group of the fastest session. Same rule as
 * `rate::rates_offered` in Rust.
 */
export function importRateOptions(sessionsHz: readonly (number | null)[]): ImportRateOption[] {
  const hz = sessionsHz.filter((h): h is number => h !== null && h > 0 && Number.isFinite(h));
  const fastest = hz.reduce((a, h) => Math.max(a, h), 0);
  const out: ImportRateOption[] = [];
  for (const r of IMPORT_RATES_HZ) {
    if (r >= fastest || !hz.every((h) => importRateDivides(h, r))) continue;
    const label = `${r} Hz (the median of each ${importGroupSize(fastest, r)})`;
    out.push({ value: r, label: r === DEFAULT_IMPORT_HZ ? `${label}, the default` : label });
  }
  out.push({ value: 'all', label: fastest > 0 ? `All readings (${hzWords(fastest)})` : 'All readings' });
  return out;
}

/** The rate an import starts with: 1 Hz when it is offered, else every reading. */
export function defaultImportRate(options: readonly ImportRateOption[]): ImportRate {
  return options.some((o) => o.value === DEFAULT_IMPORT_HZ) ? DEFAULT_IMPORT_HZ : 'all';
}

export interface RateReadings {
  readonly tMs: number[];
  readonly values: number[];
  readonly flags: number[];
}

/**
 * The groups of one session recorded at `hz`, `n` readings per stored reading (decision 266): a group is the run of
 * readings in one time bucket, k = floor((2 * hz * (t - t0) + 1000) / (2000 * n)) with t0 the session's first
 * reading, so with no gap the groups are n consecutive readings from the first (decision 225) and no group crosses
 * a gap. Each gives its median value (even: mean of the middle two), its mean time to the ms (half up) and the
 * flags of all.
 */
function reduceGroups(
  tMs: ArrayLike<number>,
  values: ArrayLike<number>,
  flags: ArrayLike<number> | null,
  from: number,
  to: number,
  hz: number | null,
  n: number,
  out: RateReadings,
): void {
  if (from >= to) return;
  const t0 = tMs[from]!;
  const bucket = (t: number): number => Math.floor((2 * (hz ?? 0) * (t - t0) + 1000) / (2000 * n));
  let i = from;
  while (i < to) {
    const k = bucket(tMs[i]!);
    let end = i + 1;
    // n = 1 keeps every reading
    while (n > 1 && end < to && bucket(tMs[end]!) === k) end++;
    const c = end - i;
    const g: number[] = [];
    let sum = 0;
    let f = 0;
    for (let j = i; j < end; j++) {
      g.push(values[j]!);
      sum += tMs[j]!;
      f |= flags ? flags[j]! : 0;
    }
    g.sort((a, b) => a - b);
    const m = c >> 1;
    out.values.push(c % 2 ? g[m]! : (g[m - 1]! + g[m]!) / 2);
    out.tMs.push(Math.floor((2 * sum + c) / (2 * c)));
    out.flags.push(f);
    i = end;
  }
}

/**
 * A series at `targetHz` (decisions 225 and 266), each session on its own at its own rate: `sessions` are in stored
 * order with their stored reading counts and rates (`imported_hz`, else `native_hz`); when their counts do not add
 * up to the readings, the whole series is one session at `seriesHz`. Mirrors `ParsedBasemag::at_rate` in Rust. The
 * 1 Hz `.raw` export of a series stored above 1 Hz uses it, so it equals a 1 Hz import of a series stored with every
 * reading. A stored rate that 1 Hz does not divide (none since decision 265) is grouped by round(rate) readings per
 * bucket.
 */
export function reduceToRate(
  tMs: ArrayLike<number>,
  values: ArrayLike<number>,
  flags: ArrayLike<number> | null,
  sessions: readonly Pick<BasemagSession, 'reading_count' | 'native_hz' | 'imported_hz'>[],
  seriesHz: number | null,
  targetHz: number,
): RateReadings {
  const n = values.length;
  const out: RateReadings = { tMs: [], values: [], flags: [] };
  const total = sessions.reduce((a, s) => a + s.reading_count, 0);
  const parts =
    total === n && sessions.length > 0
      ? sessions.map((s) => ({ count: s.reading_count, hz: s.imported_hz ?? s.native_hz }))
      : [{ count: n, hz: seriesHz }];
  let pos = 0;
  for (const p of parts) {
    reduceGroups(tMs, values, flags, pos, pos + p.count, p.hz, importGroupSize(p.hz, targetHz), out);
    pos += p.count;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- sessions (decision 226)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `16 Aug 06:12` in the project offset. */
function dayMonthTime(ms: number, offsetH: number): string {
  const d = new Date(ms + offsetH * 3_600_000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${localParts(ms, offsetH).hms.slice(0, 5)}`;
}

/**
 * The session as the Diurnal tool lists it for the choice: `Session 2: 16 Aug 06:12 → 16 Aug 14:40 (~8.5h)`, in the
 * project offset, then its rate.
 */
export function sessionLabel(s: BasemagSession, offsetH: number): string {
  const a = Date.parse(s.started_at);
  const b = Date.parse(s.ended_at);
  const h = Math.round(((b - a) / 3_600_000) * 10) / 10;
  const rate = s.native_hz !== null ? `, ${hzWords(s.native_hz)}` : '';
  return `Session ${s.index}: ${dayMonthTime(a, offsetH)} → ${dayMonthTime(b, offsetH)} (~${h.toFixed(1)}h)${rate}`;
}

/**
 * Files of an import that need choices: a rate above 1 Hz or more than one session. `sessionsHz` is the native rate
 * of every session, for the rates offered (`importRateOptions`).
 */
export function importChoicesNeeded(files: readonly BasemagInspectFile[]): {
  readonly fastestHz: number | null;
  readonly sessionsHz: readonly (number | null)[];
  readonly multiSession: readonly BasemagInspectFile[];
  /** One sentence per file read in a chosen year (decision 488). */
  readonly yearNotes: readonly string[];
  readonly needed: boolean;
} {
  let fastest: number | null = null;
  const sessionsHz: (number | null)[] = [];
  for (const f of files)
    for (const s of f.sessions) {
      sessionsHz.push(s.native_hz);
      if (s.native_hz !== null) fastest = Math.max(fastest ?? 0, s.native_hz);
    }
  const multiSession = files.filter((f) => f.sessions.length > 1);
  // decision 488: the year chosen for a file without one is shown before the import
  const yearNotes = files.map(yearNoteText).filter((t): t is string => t !== null);
  return {
    fastestHz: fastest,
    sessionsHz,
    multiSession,
    yearNotes,
    needed: (fastest !== null && fastest > 1) || multiSession.length > 0 || yearNotes.length > 0,
  };
}
